import React, { useMemo, useState } from 'react'
import { Link } from '@inertiajs/react'
import { toast } from 'sonner'
import { Undo2, Plus, Search, X, CheckCircle2, Wallet } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { cn } from '../../lib/utils'

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || ''
const money = (v) => (v == null || v === '' ? null : `R${Number(v).toFixed(2)}`)
const STATUS_STYLE = {
  lodged: 'bg-amber-100 text-amber-800', requested: 'bg-blue-100 text-blue-800',
  refunded: 'bg-emerald-100 text-emerald-800', credited: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-700',
}
const NEXT = { lodged: ['requested', 'refunded', 'credited', 'rejected'], requested: ['refunded', 'credited', 'rejected'],
  refunded: ['requested'], credited: ['requested'], rejected: ['requested'] }

export default function Returns({ returns: initial, items, reasons, methods, statuses, owed_back, open_count, default_by }) {
  const [rows, setRows] = useState(initial)
  const [owed, setOwed] = useState(owed_back)
  const [adding, setAdding] = useState(false)

  async function patch(id, body) {
    const res = await fetch(`/stock_returns/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(), Accept: 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.ok) { toast.error(data.error || 'Could not update'); return }
    setRows((rs) => rs.map((r) => (r.id === id ? data.return : r)))
    setOwed(data.owed_back)
    toast.success('Updated')
  }

  function onCreated(r, newOwed) { setRows((rs) => [r, ...rs]); setOwed(newOwed); setAdding(false) }
  const openRows = rows.filter((r) => r.open)

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl pb-24">
        <div className="mb-5 flex items-center gap-3">
          <Undo2 className="text-brand-primary" />
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-brand-ink">Returns</h1>
            <p className="text-sm text-brand-muted">What went back, why, and what's still owed to you.</p>
          </div>
          <Link href="/stock_takes" className="rounded-full border border-brand-border bg-white px-3 py-1 text-xs font-medium text-brand-ink">← Stock take</Link>
        </div>

        {/* Owed-back banner */}
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <Wallet className="text-emerald-700" size={20} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-900">{money(owed) || 'R0.00'} still owed back to you</p>
            <p className="text-xs text-emerald-800">{open_count ?? openRows.length} open return(s) awaiting refund or credit</p>
          </div>
        </div>

        {!adding && (
          <button onClick={() => setAdding(true)}
            className="mb-5 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white active:scale-[.99]">
            <Plus size={16} /> Lodge a return
          </button>
        )}
        {adding && <AddReturn items={items} reasons={reasons} methods={methods} defaultBy={default_by} onClose={() => setAdding(false)} onCreated={onCreated} />}

        <h2 className="mb-2 mt-6 px-1 text-xs font-semibold uppercase tracking-wider text-brand-muted">All returns</h2>
        <div className="divide-y divide-brand-border overflow-hidden rounded-2xl border border-brand-border bg-white">
          {rows.length === 0 && <p className="px-4 py-8 text-center text-sm text-brand-muted">No returns logged yet.</p>}
          {rows.map((r) => <ReturnRow key={r.id} r={r} statuses={statuses} onStatus={(s) => patch(r.id, { status: s })} onRefund={(amt) => patch(r.id, { status: 'refunded', refund_amount: amt })} />)}
        </div>
      </div>
    </DashboardLayout>
  )
}

function ReturnRow({ r, onStatus, onRefund }) {
  const owed = r.open ? money(r.refund_expected) : null
  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-brand-ink">{r.item_name}</p>
          <p className="text-xs text-brand-muted">
            {r.quantity ? `${Number(r.quantity)}× · ` : ''}{r.reason_category || '—'}{r.supplier ? ` · ${r.supplier}` : ''}
          </p>
          <p className="mt-0.5 text-xs text-brand-muted">
            Returned {r.returned_on ? r.returned_on.slice(0, 10) : '—'}{r.return_method ? ` · via ${r.return_method}` : ''}
          </p>
          <p className="mt-0.5 text-xs text-brand-muted">
            Submitted by {r.submitted_by || '—'}{r.submitted_at ? ` on ${r.submitted_at.slice(0, 10)}` : ''}
          </p>
          {r.reason && <p className="mt-0.5 text-xs italic text-brand-muted">“{r.reason}”</p>}
        </div>
        <span className={cn('shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold', STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-700')}>{r.status}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {owed && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">owed {owed}</span>}
        {r.refund_amount && !r.open && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">refunded {money(r.refund_amount)}</span>}
        {(NEXT[r.status] || []).map((s) => (
          <button key={s} onClick={() => (s === 'refunded' ? onRefund(r.refund_expected) : onStatus(s))}
            className="rounded-full border border-brand-border bg-white px-2.5 py-0.5 text-xs font-medium text-brand-ink active:scale-95">
            {s === 'refunded' ? '✓ refunded' : s === 'requested' ? 'refund requested' : s}
          </button>
        ))}
      </div>
    </div>
  )
}

function AddReturn({ items, reasons, methods, defaultBy, onClose, onCreated }) {
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState(null)
  const [qty, setQty] = useState('')
  const [cat, setCat] = useState('')
  const [reason, setReason] = useState('')
  const [refund, setRefund] = useState('')
  const [method, setMethod] = useState('')
  const [returnedOn, setReturnedOn] = useState(() => new Date().toISOString().slice(0, 10))
  const [busy, setBusy] = useState(false)
  const matches = useMemo(() => {
    const n = q.trim().toLowerCase()
    if (!n) return []
    return items.filter((i) => `${i.name} ${i.supplier || ''} ${i.supplier_code || ''} ${i.barcode || ''}`.toLowerCase().includes(n)).slice(0, 20)
  }, [q, items])

  async function submit() {
    if (!picked) { toast.error('Pick the item that was returned'); return }
    setBusy(true)
    const res = await fetch('/stock_returns', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(), Accept: 'application/json' },
      body: JSON.stringify({ stock_item_id: picked.id, quantity: qty, reason_category: cat, reason, return_method: method, returned_on: returnedOn, refund_expected: refund, supplier: picked.supplier }),
    })
    const data = await res.json().catch(() => ({}))
    setBusy(false)
    if (!res.ok || !data.ok) { toast.error(data.error || 'Could not lodge return'); return }
    toast.success('Return lodged')
    onCreated(data.return, data.owed_back)
  }

  const LBL = 'mb-1 block text-xs font-medium text-brand-muted'
  const INP = 'w-full rounded-xl border border-brand-border bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none'
  return (
    <div className="rounded-2xl border border-brand-border bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold text-brand-ink">Lodge a return</h2>
        <button onClick={onClose} className="rounded-full p-1 text-brand-muted hover:bg-brand-surface active:scale-90"><X size={18} /></button>
      </div>
      {!picked ? (
        <>
          <label className={LBL}>Which item was returned?</label>
          <div className="relative mb-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item, code, supplier, barcode…" className={cn(INP, 'pl-9')} />
          </div>
          {matches.length > 0 && (
            <div className="max-h-56 divide-y divide-brand-border overflow-y-auto rounded-xl border border-brand-border">
              {matches.map((i) => (
                <button key={i.id} onClick={() => { setPicked(i); setQ('') }} className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm hover:bg-brand-surface">
                  <span className="truncate text-brand-ink">{i.name}{i.supplier ? <span className="text-brand-muted"> · {i.supplier}</span> : null}</span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between rounded-xl bg-brand-surface px-3 py-2">
            <span className="text-sm font-medium text-brand-ink">{picked.name}<span className="text-brand-muted"> · {picked.supplier || 'no supplier'}</span></span>
            <button onClick={() => setPicked(null)} className="text-xs text-brand-primary">change</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label><span className={LBL}>Quantity</span><input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" placeholder="e.g. 2" className={INP} /></label>
            <label><span className={LBL}>Expected refund (R)</span><input value={refund} onChange={(e) => setRefund(e.target.value)} inputMode="decimal" placeholder="optional" className={INP} /></label>
          </div>
          <label className="mt-3 block"><span className={LBL}>Reason</span>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className={INP}>
              <option value="">Choose a reason…</option>
              {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <label><span className={LBL}>How was it returned?</span>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className={INP}>
                <option value="">Choose a method…</option>
                {(methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label><span className={LBL}>Date returned</span>
              <input type="date" value={returnedOn} onChange={(e) => setReturnedOn(e.target.value)} className={INP} /></label>
          </div>
          <label className="mt-3 block"><span className={LBL}>Anything particular to this return? (optional)</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. wrong size, arrived cracked, waybill 12345" className={INP} /></label>
          <button disabled={busy} onClick={submit} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            <CheckCircle2 size={16} /> {busy ? 'Lodging…' : 'Lodge return'}
          </button>
        </>
      )}
    </div>
  )
}
